import type { CompareResult } from "alcalzone-shared/comparable";
import {
	type DeferredPromise,
	createDeferredPromise,
} from "alcalzone-shared/deferred-promise";
import { SortedList } from "alcalzone-shared/sorted-list";
import { createWrappingCounter } from "./wrappingCounter.js";
import { evalOrStatic, highResTimestamp, noop } from "./utils.js";

/** A high-level task that can be started and stepped through */
export interface Task<
	TReturn,
	TaskTag extends { id: string } = { id: string },
	TError extends Error = Error,
> {
	readonly id: number;
	readonly timestamp: number;
	readonly builder: TaskBuilder<TReturn, TaskTag>;

	/** The parent task spawning this subtask, if any */
	readonly parent?: Task<unknown, TaskTag, TError>;

	/** A name to identify the task */
	readonly name?: string;
	/** A tag to identify the task programmatically */
	readonly tag?: TaskTag;
	/** The task's priority */
	readonly priority: TaskPriority;
	/** The concurrency group this task belongs to */
	readonly group?: TaskConcurrencyGroup;
	/** How the task should behave when interrupted */
	readonly interrupt: TaskInterruptBehavior;
	/** Starts the task it if hasn't been started yet, and executes the next step of the task */
	step(): Promise<TaskStepResult<TReturn, TaskTag>>;
	/** Stops the task without further executing it, cleans up, and prepares it for starting again */
	reset(): Promise<void>;
	/** Resolves the task's promise to notify the caller */
	resolve(result: TReturn): void;
	/** Rejects the task's promise to notify the caller */
	reject(error: TError): void;
	/** The current state of the task */
	get state(): TaskState;

	readonly generator: ReturnType<TaskBuilder<TReturn>["task"]> | undefined;
	readonly promise: Promise<TReturn>;
}

function getTaskName(task: Task<unknown>): string {
	return `${
		task.name ?? (task.tag && JSON.stringify(task.tag)) ?? "unnamed"
	} (ID ${task.id})`;
}

/** Defines the necessary information for creating a task */
export interface TaskBuilder<
	TReturn,
	TaskTag extends { id: string } = { id: string },
	TInner = unknown,
> {
	/** A name to identify the task */
	name?: string;
	/** A tag to identify the task programmatically */
	tag?: TaskTag;
	/** The task's priority */
	priority: TaskPriority;
	/** How the task should behave when interrupted */
	interrupt?: TaskInterruptBehavior;
	/** The concurrency group this task belongs to */
	group?: TaskConcurrencyGroup;
	/**
	 * The task's main generator function. This is called repeatedly until it's done.
	 * The function must yield at points where it may be interrupted.
	 *
	 * At those points, the task can also wait for something to happen:
	 * - Either a Promise, in which case it must yield a function that returns that Promise
	 * - Or another task, in which case it must yield a TaskBuilder object or a function that returns one
	 *
	 * Yielded Promises should not spawn new tasks. If they do, the spawned tasks MUST have a higher priority than the parent task.
	 */
	task: () => AsyncGenerator<
		| (() => Promise<unknown> | TaskBuilder<unknown, TaskTag, unknown>)
		| (() => TaskBuilder<unknown, TaskTag, unknown>)
		| TaskBuilder<unknown, TaskTag, unknown>
		| undefined,
		TReturn,
		TInner
	>;
	/** A cleanup function that gets called when the task is dropped */
	cleanup?: () => Promise<void>;
}

export type TaskReturnType<T extends TaskBuilder<unknown>> =
	T extends TaskBuilder<infer R> ? R : never;

function isTaskBuilder<T>(obj: any): obj is TaskBuilder<T> {
	return (
		typeof obj === "object" &&
		typeof obj.task === "function" &&
		typeof obj.priority === "number"
	);
}

/**
 * The priority of a task.
 *
 * Higher priority tasks are executed first and interrupt lower priority tasks.
 * The recommended priority for application-initiated communication is `Normal`.
 * `Low` and `Lower` are recommended for internal long-running tasks that should not interfere with user-initiated tasks.
 * `Idle` is recommended for tasks that should only run when no other tasks are pending.
 */
export enum TaskPriority {
	Highest,
	High = 1,
	Normal = 2,
	Low = 3,
	Lower = 4,
	Idle = 5,
}

export enum TaskState {
	/** The task has not been created yet */
	None,
	/** The task is being executed */
	Active,
	/** The task is waiting for a Promise to resolve */
	AwaitingPromise,
	/** The task is waiting for another task to finish */
	AwaitingTask,
	/** The task is finished */
	Done,
}

export enum TaskInterruptBehavior {
	/** The task may not be interrupted */
	Forbidden,
	/** The task will be resumed after being interrupted (default) */
	Resume,
	/** The task needs to be restarted after being interrupted */
	Restart,
}

export type TaskStepResult<
	T,
	TaskTag extends { id: string } = { id: string },
> =
	| {
			newState: TaskState.Done;
			result: T;
	  }
	| {
			newState: TaskState.Active;
	  }
	| {
			// The task was reset while the step was in flight. The result is stale and must be dropped.
			newState: TaskState.None;
	  }
	| {
			newState: TaskState.AwaitingPromise;
			promise: Promise<unknown>;
	  }
	| {
			newState: TaskState.AwaitingTask;
			task: Task<unknown, TaskTag>;
	  };

/** Identifies a concurrency group. Tasks within the same group cannot run concurrently. */
export interface TaskConcurrencyGroup {
	id: string;
}

function compareTasks<T1, T2>(a: Task<T1>, b: Task<T2>): CompareResult {
	// In the same concurrency group, only one task may be active at a time.
	const aConcurrencyGroup = a.group?.id;
	const bConcurrencyGroup = b.group?.id;
	if (
		aConcurrencyGroup &&
		bConcurrencyGroup &&
		aConcurrencyGroup === bConcurrencyGroup
	) {
		const aActiveOrWaiting =
			a.state === TaskState.Active ||
			a.state === TaskState.AwaitingPromise ||
			a.state === TaskState.AwaitingTask;
		const bActiveOrWaiting =
			b.state === TaskState.Active ||
			b.state === TaskState.AwaitingPromise ||
			b.state === TaskState.AwaitingTask;

		if (aActiveOrWaiting && !bActiveOrWaiting) return 1; // A is already active
		if (!aActiveOrWaiting && bActiveOrWaiting) return -1; // B is already active

		// None of the tasks is active, fall back to default sorting
	}

	// Sort by priority for tasks not in the same concurrency group
	if (a.priority < b.priority) return 1;
	if (a.priority > b.priority) return -1;

	// Deprioritize waiting tasks
	const aWaiting =
		a.state === TaskState.AwaitingPromise ||
		a.state === TaskState.AwaitingTask;
	const bWaiting =
		b.state === TaskState.AwaitingPromise ||
		b.state === TaskState.AwaitingTask;
	if (!aWaiting && bWaiting) return 1;
	if (aWaiting && !bWaiting) return -1;

	// Sort equal priority by timestamp. Newer tasks go to the end of the list
	if (a.timestamp < b.timestamp) return 1;
	if (a.timestamp > b.timestamp) return -1;

	// If all else fails, sort by ID
	return Math.sign(b.id - a.id) as CompareResult;
}

export class TaskScheduler<
	TaskTag extends { id: string } = { id: string },
	TError extends Error = Error,
> {
	public constructor(
		private defaultErrorFactory: () => TError = () =>
			new Error("Task was removed") as any,
		private verbose: boolean = false,
	) {}

	private _tasks = new SortedList<Task<unknown, TaskTag>>(
		undefined,
		compareTasks,
	);
	private _currentTask: Task<unknown, TaskTag> | undefined;

	private _idGenerator = createWrappingCounter(0xff_ff_ff);
	private _continueSignal: DeferredPromise<void> | undefined;
	private _stopSignal: DeferredPromise<void> | undefined;
	private _stopPromise: DeferredPromise<void> | undefined;

	public queueTask<T>(builder: TaskBuilder<T, TaskTag>): Promise<T> {
		const task = this.createTask(builder);
		this._tasks.add(task);
		if (this.verbose) {
			console.log(`Task queued: ${getTaskName(task)}`);
		}

		if (this._continueSignal) this._continueSignal.resolve();
		return task.promise;
	}

	/** Removes/stops tasks matching the given predicate. Returns `true` when a task was removed, `false` otherwise. */
	public async removeTasks(
		predicate: (task: Task<unknown, TaskTag>) => boolean,
		reason?: TError,
	): Promise<boolean> {
		// Collect tasks that should be removed, so that we handle the current task last
		const tasksToRemove: Task<unknown, TaskTag>[] = [];
		let currentTaskToRemove: Task<unknown, TaskTag> | undefined;
		for (const task of this._tasks) {
			if (predicate(task)) {
				if (task === this._currentTask) {
					currentTaskToRemove = task;
				} else {
					tasksToRemove.push(task);
				}
			}
		}
		if (currentTaskToRemove) {
			tasksToRemove.push(currentTaskToRemove);
		}

		reason ??= this.defaultErrorFactory();

		for (const task of tasksToRemove) {
			// Skip tasks the run loop finalized during a previous iteration's await
			if (this._currentTask !== task && !this._tasks.contains(task)) {
				continue;
			}
			await this.dropTask(task, reason);
		}

		if (this._continueSignal) this._continueSignal.resolve();

		return tasksToRemove.length > 0;
	}

	/** Removes a single task from the queue, resets it and rejects its promise */
	private async dropTask(
		task: Task<unknown, TaskTag>,
		reason: TError,
	): Promise<void> {
		if (this.verbose) {
			console.log(`Removing task: ${getTaskName(task)}`);
		}
		this._tasks.remove(task);
		// Claim the task before awaiting anything, so the run loop drops the
		// result of an in-flight step instead of finalizing the task a second time
		if (this._currentTask === task) {
			this._currentTask = undefined;
		}
		await task.reset().catch(noop);
		task.reject(reason);
		// Re-add the parent task to the list if there is one
		if (task.parent) {
			if (this.verbose) {
				console.log(
					`Restoring parent task: ${getTaskName(task.parent)}`,
				);
			}
			this._tasks.add(task.parent);
		}
	}

	public findTask<T = unknown>(
		predicate: (task: Task<T, TaskTag>) => boolean,
	): Promise<T> | undefined {
		return this._tasks.find((t: any) => predicate(t))?.promise as
			| Promise<T>
			| undefined;
	}

	/** Creates a task that can be executed */
	private createTask<T>(
		builder: TaskBuilder<T, TaskTag>,
		parent?: Task<unknown, TaskTag>,
	): Task<T, TaskTag, TError> {
		let state = TaskState.None;
		let generator: ReturnType<TaskBuilder<T, TaskTag>["task"]> | undefined;
		let waitForPromise: Promise<unknown> | undefined;
		const promise = createDeferredPromise<T>();

		let prevResult: unknown;
		let waitError: unknown;

		const self = this;

		return {
			id: this._idGenerator(),
			timestamp: highResTimestamp(),
			builder,
			parent,
			name: builder.name,
			tag: builder.tag,
			priority: builder.priority,
			group: builder.group,
			interrupt: builder.interrupt ?? TaskInterruptBehavior.Resume,
			promise,
			async step() {
				// Do not proceed while still waiting for a Promise to resolve
				if (state === TaskState.AwaitingPromise && waitForPromise) {
					return {
						newState: state,
						promise: waitForPromise,
					};
				} else if (state === TaskState.AwaitingTask) {
					throw new Error(
						"Cannot step through a task that is waiting for another task",
					);
				}

				generator ??= builder.task();
				state = TaskState.Active;

				// Capture the generator so a concurrent reset() can be detected after the await
				const gen = generator;
				let genResult: Awaited<ReturnType<typeof gen.next>>;
				try {
					genResult = waitError
						? await gen.throw(waitError)
						: await gen.next(prevResult);
				} catch (e) {
					// Drop errors from a step whose task was reset in the meantime
					if (generator !== gen) return { newState: TaskState.None };
					throw e;
				}
				// Drop the step result if the task was reset while the generator was running,
				// so the task state is not corrupted after the reset
				if (generator !== gen) return { newState: TaskState.None };

				const { value, done } = genResult;
				prevResult = undefined;
				waitError = undefined;
				if (done) {
					state = TaskState.Done;
					return {
						newState: state,
						result: value,
					};
				} else if (value != undefined) {
					const waitFor = evalOrStatic(value);
					if (waitFor instanceof Promise) {
						state = TaskState.AwaitingPromise;
						waitForPromise = waitFor
							.then((result) => {
								prevResult = result;
							})
							.catch((e) => {
								waitError = e;
							})
							.finally(() => {
								waitForPromise = undefined;
								if (state === TaskState.AwaitingPromise) {
									state = TaskState.Active;
								}
							});
						return {
							newState: state,
							promise: waitForPromise,
						};
					} else if (isTaskBuilder(waitFor)) {
						if (waitFor.priority > builder.priority) {
							throw new Error(
								"Tasks cannot yield to tasks with lower priority than their own",
							);
						}
						// Create a sub-task with a reference to this task
						state = TaskState.AwaitingTask;
						const subTask = self.createTask(waitFor, this);

						subTask.promise
							.then((result) => {
								prevResult = result;
							})
							.catch((e) => {
								waitError = e;
							})
							.finally(() => {
								if (state === TaskState.AwaitingTask) {
									state = TaskState.Active;
								}
							});

						return {
							newState: state,
							task: subTask,
						};
					} else {
						throw new Error("Invalid value yielded by task");
					}
				} else {
					return { newState: state };
				}
			},
			async reset() {
				if (state === TaskState.None) return;
				state = TaskState.None;
				waitForPromise = undefined;
				generator = undefined;
				prevResult = undefined;
				waitError = undefined;

				await builder.cleanup?.();
			},
			resolve(result) {
				promise.resolve(result);
			},
			reject(error) {
				promise.reject(error);
			},
			get state() {
				return state;
			},
			get generator() {
				return generator;
			},
		};
	}

	public start(): void {
		this._stopSignal = createDeferredPromise();
		setImmediate(async () => {
			try {
				await this.run();
			} catch (e) {
				console.error("Task runner crashed", e);
			}
		});
	}

	private async run(): Promise<void> {
		while (true) {
			let waitFor: Promise<unknown> | undefined;
			if (this._tasks.length > 0) {
				const firstTask = this._tasks.peekStart()!;
				if (!this._currentTask) {
					// We're not currently executing a task, start executing the first one
					this._currentTask = firstTask;
				} else if (
					this._currentTask !== firstTask &&
					this._currentTask.interrupt !==
						TaskInterruptBehavior.Forbidden
				) {
					// We are executing an interruptible task, and a new task with a higher priority was added
					if (
						this._currentTask.interrupt ===
						TaskInterruptBehavior.Restart
					) {
						// The current task needs to be restarted after being interrupted, so reset it
						const interrupted = this._currentTask;
						await interrupted.reset();
						// Re-evaluate the queue if it changed during the reset
						if (
							this._currentTask !== interrupted ||
							this._tasks.peekStart() !== firstTask
						) {
							continue;
						}
					}
					// switch to the new task
					this._currentTask = firstTask;
				}

				// Capture the current task, so concurrent removals can be detected after each await
				const task = this._currentTask;

				if (this.verbose) {
					console.log(`Stepping through task: ${getTaskName(task)}`);
				}

				const finalizeCurrentTask = async () => {
					this._tasks.remove(task);
					// Claim the task before awaiting, so a concurrent removeTasks() skips it
					this._currentTask = undefined;
					await task.reset();
					// Re-add the parent task to the list if there is one
					if (task.parent) {
						if (this.verbose) {
							console.log(
								`Restoring parent task: ${getTaskName(
									task.parent,
								)}`,
							);
						}
						this._tasks.add(task.parent);
					}
				};

				// Execute the current task one step further
				waitFor = undefined;
				let stepResult: TaskStepResult<unknown, TaskTag>;
				try {
					stepResult = await task.step();
				} catch (e) {
					// Drop the error if the task was removed during the step. It was already rejected.
					if (this._currentTask !== task) continue;

					if (this.verbose) {
						console.error(`- Task threw an error:`, e);
					}
					// The task threw an error, expose the result and clean up.
					task.reject(e as Error);
					await finalizeCurrentTask();
					// Then continue with the next iteration
					continue;
				}

				// Drop the step result if the task was removed during the step. It was already rejected.
				if (this._currentTask !== task) continue;

				if (stepResult.newState === TaskState.Done) {
					if (this.verbose) {
						console.log(`- Task finished`);
					}
					// The task is done, clean up
					task.resolve(stepResult.result);
					await finalizeCurrentTask();
					// Then continue with the next iteration
					continue;
				} else if (stepResult.newState === TaskState.AwaitingPromise) {
					// The task is waiting for something
					if (this.verbose) {
						console.log(`- Task waiting`);
					}

					// If the task may be interrupted, check if there are other same-priority tasks that should be executed instead
					if (task.interrupt !== TaskInterruptBehavior.Forbidden) {
						// Re-queue the task, so the queue gets reordered
						this._tasks.remove(task);
						this._tasks.add(task);

						if (this._tasks.peekStart() !== task) {
							if (this.verbose) {
								console.log(`-- Continuing with another task`);
							}
							// The task is no longer the first in the queue. Switch to the other one
							continue;
						}
					}

					// Otherwise, we got nothing to do right now than to wait
					waitFor = stepResult.promise;
				} else if (stepResult.newState === TaskState.AwaitingTask) {
					// The task spawned a sub-task. Replace it with the sub-task and continue executing
					if (this.verbose) {
						console.log(`- Task spawned a sub-task`);
					}
					this._tasks.add(stepResult.task);
					this._tasks.remove(task);
					// Continue with the next iteration
					continue;
				} else {
					// The current task is not done, or the step result is stale after a removal.
					// Continue with the next iteration.
					continue;
				}
			}

			// Task queue empty. Wait for either a new task or the stop signal,
			// or the current task to finish waiting
			this._continueSignal = createDeferredPromise();
			const nextAction = await Promise.race(
				[
					this._continueSignal.then(() => "continue" as const),
					this._stopSignal!.then(() => "stop" as const),
					waitFor?.then(() => "continue" as const),
				].filter((p) => p !== undefined),
			);
			this._continueSignal = undefined;
			if (nextAction === "stop") break;
		}
		this._stopPromise!.resolve();
	}

	public async stop(): Promise<void> {
		if (!this._stopSignal) return;
		// Signal to the task runner that it should stop
		this._stopPromise = createDeferredPromise();
		this._stopSignal.resolve();
		await this._stopPromise;
	}
}
